/** 1er janvier de l'année en cours → 31 décembre de l'année suivante. */
export const defaultFilterDates = () => {
  const year = new Date().getFullYear();
  return {
    dateFrom: `${year}-01-01`,
    dateTo: `${year + 1}-12-31`,
  };
};

export const toInputDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** Période rapide → dates affichées dans les champs début / fin. */
export const datesForPeriod = (value) => {
  const start = new Date();
  const end = new Date();
  switch (value) {
    case "aujourd'hui":
      break;
    case 'hier':
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      break;
    case 'dernier_7_jours':
      start.setDate(start.getDate() - 7);
      break;
    case 'dernier_30_jours':
      start.setDate(start.getDate() - 30);
      break;
    case 'ce_mois':
      start.setDate(1);
      end.setMonth(end.getMonth() + 1, 0);
      break;
    case 'dernier_mois':
      start.setMonth(start.getMonth() - 1, 1);
      end.setDate(0);
      break;
    case 'this_year':
      start.setMonth(0, 1);
      end.setMonth(11, 31);
      break;
    default:
      return defaultFilterDates();
  }
  return { dateFrom: toInputDate(start), dateTo: toInputDate(end) };
};

export const formatDate = (dateString) => {
  const options = { day: 'numeric', month: 'long', year: 'numeric' };

  // S'assurer que le fuseau horaire est bien pris en compte
  const date = new Date(dateString);

  // Si la date n'est pas valide, renvoyer une chaîne vide (gestion d'erreurs)
  if (isNaN(date.getTime())) {
    return '';
  }

  const formattedDate = date.toLocaleDateString('fr-FR', options);
  return formattedDate;
};


export default function formatNumber(number) {
  if (isNaN(number)) return number; // Si ce n'est pas un nombre, retournez-le tel quel.

  // Convertir le nombre en entier (en retirant les décimales)
  const intPart = Math.round(number);

  // Ajouter des points tous les trois chiffres à la partie entière
  return String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Libellé magasin pour un produit (liste articles). */
export const getProductMagasinLabel = (product) =>
  product?.magasin_label
  ?? product?.magasin?.code
  ?? product?.magasin?.name
  ?? null;

/** Libellé magasin (Mazawin X/Y…) pour une variante caisse ou une ligne de facture. */
export const getVariantMagasinLabel = (variant) =>
  variant?.magasin_label
  ?? variant?.magasin?.code
  ?? variant?.magasin?.name
  ?? null;

export const getOrderLineWarehouseCode = (item) =>
  item?.magasin_label
  ?? item?.magasin_code
  ?? item?.variant?.magasin_label
  ?? item?.variant?.magasin?.code
  ?? item?.variant?.magasin?.name
  ?? '-';

/** Groupe fiscal ligne : A = exonéré, B = taxable. */
export const lineTaxGroupLabel = (item) => {
  if (item?.taxGroup === 'B' || item?.taxGroup === 'A') {
    return item.taxGroup;
  }
  return item?.variant?.is_taxable ? 'B' : 'A';
};

/** Quantité stock (somme des variantes ou stock_quantity API). */
export const getProductStockQuantity = (product) => {
  if (product?.stock_quantity != null) {
    return parseFloat(product.stock_quantity) || 0;
  }
  return (
    product?.variants?.reduce((acc, variant) => acc + parseFloat(variant.quantity || 0), 0) || 0
  );
};

/** Variante de référence pour cartons / pièces / m² (comme en caisse). */
export const getPrimaryVariant = (product) => {
  const variants = product?.variants || [];
  if (!variants.length) return null;
  return (
    variants.find((v) => v.nb_pieces_par_carton || v.nb_m2_par_carton) || variants[0]
  );
};

/** Objet produit attendu par generateQuantityDescription (aligné vente /commandes). */
export const buildProductForQuantityDescription = (product) => {
  const variant = getPrimaryVariant(product);
  return {
    category_id: Number(product?.category_id),
    unit_id: Number(product?.unit_id ?? product?.unit?.id),
    unit_symbol: product?.unit?.symbol ?? product?.unit_symbol,
    unit_name: product?.unit?.name ?? product?.unit_name,
    nb_m2_par_carton: parseFloat(variant?.nb_m2_par_carton ?? 0) || 0,
    nb_m2_par_piece: parseFloat(variant?.nb_m2_par_piece ?? 0) || 0,
    nb_pieces_par_carton:
      parseInt(variant?.nb_pieces_par_carton ?? product?.pieces_per_carton ?? 0, 10) || 0,
  };
};

/** Libellé stock détaillé : ex. « 2 carton(s) et 4 pièce(s) » ou « 3 carton(s), 2 pièce(s) et 1,50 m² ». */
export const formatProductStockDisplay = (product) => {
  const qty = getProductStockQuantity(product);
  const description = generateQuantityDescription(
    qty,
    buildProductForQuantityDescription(product)
  );
  return { qty, display: description };
};

export const formatDateWithTime = (dateString) => {
  // Convertir la chaîne en objet Date
  const date = new Date(dateString);

  // Vérifier si la date est valide
  if (isNaN(date.getTime())) {
    return '';
  }

  // Options pour le formatage en français
  const options = {
    year: 'numeric',   // Année complète (ex : 2024)
    month: 'numeric',     // Mois complet (ex : novembre)
    day: 'numeric',    // Jour du mois (ex : 27)
    hour: '2-digit',   // Heure (ex : 20)
    minute: '2-digit', // Minute (ex : 32)
  };

  // Formater la date et l'heure en français
  const formattedDate = date.toLocaleDateString('fr-FR', options);

  // Ajouter "à" entre la date et l'heure
  return formattedDate;
}

const FRENCH_UNITS = [
  'zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
  'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf',
];

const FRENCH_TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];

/** Convertit un entier (0–99) en lettres françaises. Retourne '' pour 0 (composant). */
function frenchUnder100(n, followedByMore = false) {
  if (n === 0) return '';
  if (n < 20) return FRENCH_UNITS[n];

  if (n < 70) {
    const ten = FRENCH_TENS[Math.floor(n / 10)];
    const unit = n % 10;
    if (unit === 0) return ten;
    if (unit === 1) return `${ten}-et-un`;
    return `${ten}-${FRENCH_UNITS[unit]}`;
  }

  if (n < 80) {
    const rest = n - 60;
    if (rest === 0) return 'soixante';
    if (rest === 1) return 'soixante et un';
    return `soixante-${frenchUnder100(rest)}`;
  }

  const rest = n - 80;
  if (rest === 0) return followedByMore ? 'quatre-vingt' : 'quatre-vingts';
  return `quatre-vingt-${frenchUnder100(rest)}`;
}

/** Convertit un entier (0–999) en lettres françaises. */
function frenchUnder1000(n, followedByMore = false) {
  if (n === 0) return '';

  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  let words = '';

  if (hundreds === 1) {
    words = 'cent';
  } else if (hundreds > 1) {
    words = `${FRENCH_UNITS[hundreds]} cent`;
    if (rest === 0 && !followedByMore) words += 's';
  }

  const restWords = frenchUnder100(rest, followedByMore);
  if (restWords) {
    words = words ? `${words} ${restWords}` : restWords;
  }

  return words;
}

/** Convertit un entier positif en lettres françaises. */
function frenchIntegerToWords(n, followedByMore = false) {
  if (n === 0) return followedByMore ? '' : 'zéro';

  const scales = [
    { value: 1_000_000_000, singular: 'milliard', plural: 'milliards' },
    { value: 1_000_000, singular: 'million', plural: 'millions' },
    { value: 1_000, singular: 'mille', plural: 'mille' },
  ];

  for (const scale of scales) {
    if (n >= scale.value) {
      const quotient = Math.floor(n / scale.value);
      const remainder = n % scale.value;

      let head;
      if (scale.singular === 'mille' && quotient === 1) {
        head = 'mille';
      } else {
        const qWords = frenchIntegerToWords(quotient, true);
        const label = quotient > 1 ? scale.plural : scale.singular;
        head = `${qWords} ${label}`.trim();
      }

      if (remainder === 0) return head;
      return `${head} ${frenchIntegerToWords(remainder, followedByMore)}`.trim();
    }
  }

  return frenchUnder1000(n, followedByMore);
}

/** Montant en lettres pour factures FCFA (partie entière arrondie). */
export const convertNumberToWordsInFCFA = (amount) => {
  const value = Math.round(Number(amount) || 0);
  if (value === 0) return 'zéro FCFA';
  return `${frenchIntegerToWords(value)} FCFA`;
};

/** Unité m² (Mettre carré) — cartons / pièces / m². Unité PC (Pièce) — pièces uniquement. */
export const isSquareMeterUnit = (product) => {
  const unitId = Number(product?.unit_id);
  if (unitId === 6) return true;
  if (unitId === 5) return false;
  const sym = (product?.unit_symbol || '').toLowerCase().trim();
  const name = (product?.unit_name || '').toLowerCase();
  if (sym === 'm2' || sym === 'm²') return true;
  if (name.includes('mettre') || name.includes('carr')) return true;
  return Number(product?.category_id) === 17;
};

const formatPieceOnlyQuantity = (quantity) => {
  const q = Number(quantity) || 0;
  if (Number.isInteger(q) || Math.abs(q - Math.round(q)) < 1e-6) {
    return `${Math.round(q)} pièce(s)`;
  }
  return `${q.toFixed(2)} pièce(s)`;
};

export const buildVariantForQuantityDescription = (row) => ({
  category_id: Number(row?.category_id),
  unit_id: Number(row?.unit_id),
  unit_symbol: row?.unit_symbol,
  unit_name: row?.unit_name,
  nb_m2_par_carton: parseFloat(row?.nb_m2_par_carton ?? 0) || 0,
  nb_m2_par_piece: parseFloat(row?.nb_m2_par_piece ?? 0) || 0,
  nb_pieces_par_carton: parseInt(row?.nb_pieces_par_carton ?? 0, 10) || 0,
});

export const generateQuantityDescription = (quantity, product) => {
  let totalCartons = 0;
  let totalPieces = 0;
  let remainingM2 = 0;
  let description = "";

  if (!isSquareMeterUnit(product)) {
    return formatPieceOnlyQuantity(quantity);
  }

  if (isSquareMeterUnit(product)) { // Produits en m² (Mettre carré)
    const m2PerCarton = parseFloat(product.nb_m2_par_carton || 0);
    const m2PerPiece = parseFloat(product.nb_m2_par_piece || 0);

    if (m2PerCarton > 0) {
      totalCartons = Math.floor(quantity / m2PerCarton);
      remainingM2 = quantity % m2PerCarton;

      if (m2PerPiece > 0) {
        totalPieces = Math.floor(remainingM2 / m2PerPiece);
        // Ajustement pour éviter que le m² restant dépasse une pièce
        if (remainingM2 >= m2PerPiece) {
          totalPieces += 1;
          remainingM2 = 0;
        } else {
          remainingM2 = remainingM2 % m2PerPiece;
        }
      }

      description = `${totalCartons} carton(s)`;
      if (totalPieces > 0) {
        description += `, ${totalPieces} pièce(s)`;
      }
      if (remainingM2 > 0) {
        description += ` et ${remainingM2.toFixed(2)} m²`;
      }
    } else {
      const q = Number(quantity) || 0;
      description = `${q.toFixed(2)} m²`;
    }
  }

  return description;
};

export const generateQuantityDescriptionV2 = (quantity, product) => {
  let totalCartons = 0;
  let totalPieces = 0;
  let remainingM2 = 0;
  let description = "";

  if (!isSquareMeterUnit(product)) {
    return formatPieceOnlyQuantity(quantity);
  }

  if (isSquareMeterUnit(product)) { // Produits en m² (Mettre carré)
    const m2PerCarton = parseFloat(product.nb_m2_par_carton || 0);
    const m2PerPiece = parseFloat(product.nb_m2_par_piece || 0);

    if (m2PerCarton > 0) {
      totalCartons = Math.floor(quantity / m2PerCarton);
      remainingM2 = quantity % m2PerCarton;

      if (m2PerPiece > 0) {
        totalPieces = Math.floor(remainingM2 / m2PerPiece);
        // Ajustement pour éviter que le m² restant dépasse une pièce
        if (remainingM2 >= m2PerPiece) {
          totalPieces += 1;
          remainingM2 = 0;
        } else {
          remainingM2 = remainingM2 % m2PerPiece;
        }
      }

      description = `${totalCartons} carton(s)`;
      if (totalPieces > 0) {
        description += `, ${totalPieces} pièce(s)`;
      }
      if (remainingM2 > 0) {
        description += ` et ${remainingM2.toFixed(2)} m²`;
      }
    } else {
      const q = Number(quantity) || 0;
      description = `${q.toFixed(2)} m²`;
    }
  }

  return description;
};

/** Quantité brute entre parenthèses : ex. « 4 p », « 450 m2 ». */
export const formatRawQuantitySuffix = (quantity, product) => {
  const q = Number(quantity) || 0;
  if (isSquareMeterUnit(product)) {
    const formatted =
      Number.isInteger(q) || Math.abs(q - Math.round(q)) < 1e-6
        ? Math.round(q)
        : q.toFixed(2).replace(/\.?0+$/, '');
    return `${formatted} m2`;
  }
  const pieces =
    Number.isInteger(q) || Math.abs(q - Math.round(q)) < 1e-6
      ? Math.round(q)
      : q.toFixed(2).replace(/\.?0+$/, '');
  return `${pieces} p`;
};

/** Libellé détaillé + quantité brute entre parenthèses (résumé admin). */
export const formatSummaryQuantityLabel = (quantity, row) => {
  const ctx = buildVariantForQuantityDescription(row);
  const label = generateQuantityDescriptionV2(quantity, ctx);
  const raw = formatRawQuantitySuffix(quantity, ctx);
  if (!label) return `(${raw})`;
  return `${label} (${raw})`;
};

/** Libellé quantité vendue / stock à partir d'une ligne API (résumé, best-selling, etc.). */
export const formatSoldQuantityLabel = (quantity, row) => {
  const built = buildVariantForQuantityDescription(row);
  const label = generateQuantityDescriptionV2(quantity, built);
  if (label) return label;
  const q = Number(quantity) || 0;
  const unit = row?.unit_symbol || row?.unit_name || '';
  return unit ? `${formatNumber(q)} ${unit}`.trim() : formatNumber(q);
};

// Fonction pour formater la date
const formatDateTime = (dateString) => {
  if (!dateString) return ''; // Si la date est vide ou invalide

  const date = new Date(dateString);
  const options = {
    weekday: 'long', // Jour de la semaine complet (ex. "Monday")
    year: 'numeric',
    month: 'long', // Mois complet (ex. "December")
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true, // Pour afficher l'heure en format AM/PM
  };

  return date.toLocaleString('fr-FR', options); // Formater selon la locale française
};

export const arrondirAvecSeuil = (prix, seuil = 0.7) => {
  const partieEntiere = Math.floor(prix);
  const partieDecimale = prix - partieEntiere;

  if (partieDecimale >= seuil) {
    return Math.ceil(prix);
  } else {
    return Math.floor(prix);
  }
}

export function calculateTax(ttcPrice, taxable = true, tvaRate = 0.18, aibRate = 0.01) {
  // Vérification que le prix TTC est valide
  if (typeof ttcPrice !== 'number' || ttcPrice <= 0) {
    throw new Error('Le prix TTC doit être un nombre positif');
  }

  // Définir les taux effectifs
  const effectiveTvaRate = taxable ? tvaRate : 0;
  const effectiveAibRate = aibRate !== null ? aibRate : 0;

  // Calcul du facteur total (1 + taux TVA + taux AIB)
  const totalFactor = 1 + effectiveTvaRate + effectiveAibRate;

  // Calcul du prix HT
  const htPrice = ttcPrice / totalFactor;

  // Calcul de la TVA
  const tvaAmount = htPrice * effectiveTvaRate;

  // Calcul de l'AIB
  const aibAmount = htPrice * effectiveAibRate;

  // Calcul de HT + TVA
  const htPlusTva = htPrice + tvaAmount;

  // Arrondi à 2 décimales pour tous les montants
  const result = {
    prixHT: parseFloat(htPrice.toFixed(2)),
    tva: parseFloat(tvaAmount.toFixed(2)),
    aib: parseFloat(aibAmount.toFixed(2)),
    prixTTC: parseFloat(ttcPrice.toFixed(2)),
    htPlusTva: parseFloat(htPlusTva.toFixed(2))
  };

  return result;
}

export function calculateTaxItems(items, aibRate, discount = 0) {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('La liste des articles doit être un tableau non vide');
  }

  if (typeof discount !== 'number' || discount < 0) {
    throw new Error('La remise doit être un nombre positif ou zéro');
  }

  const tvaRate = 0.18;
  const effectiveAibRate = aibRate ?? 0;

  let totalHT = 0;
  let totalTVA = 0;
  let totalTTC = 0;

  const detailedItems = items.map((item) => {
    if (!item.price || !item.quantity) {
      throw new Error(`Article "${item.name}" invalide`);
    }

    const quantity = Number(item.quantity);
    const priceTTC = Number(item.price);

    if (isNaN(quantity) || quantity <= 0) {
      throw new Error(`Quantité invalide pour "${item.name}"`);
    }

    if (isNaN(priceTTC) || priceTTC <= 0) {
      throw new Error(`Prix invalide pour "${item.name}"`);
    }

    const isTaxable = item.taxGroup === 'B';
    const rate = isTaxable ? tvaRate : 0;

    // ✅ Calculs SANS ARRONDI
    const htUnit = priceTTC / (1 + rate);
    const tvaUnit = priceTTC - htUnit;

    const totalHtItem = htUnit * quantity;
    const totalTvaItem = tvaUnit * quantity;
    const totalTtcItem = priceTTC * quantity;

    // Accumulation SANS ARRONDI
    totalHT += totalHtItem;
    totalTVA += totalTvaItem;
    totalTTC += totalTtcItem;

    return {
      name: item.name,
      quantity,
      prixHT: htUnit,
      tva: tvaUnit,
      prixTTC: priceTTC,
      totalHT: totalHtItem,
      totalTVA: totalTvaItem,
      totalTTC: totalTtcItem
    };
  });

  // ✅ ARRONDI FINAL UNIQUEMENT
  const roundedHT = Math.round(totalHT);
  const roundedTVA = Math.round(totalTVA);
  const roundedTTC = Math.round(totalTTC);

  // AIB
  const totalAIB = Math.round(roundedHT * effectiveAibRate);

  // Remise
  const discountAmount = Math.round(discount);

  const finalTTC = roundedTTC + totalAIB - discountAmount;

  return {
    items: detailedItems.map(item => ({
      ...item,
      prixHT: Math.round(item.prixHT),
      tva: Math.round(item.tva),
      totalHT: Math.round(item.totalHT),
      totalTVA: Math.round(item.totalTVA),
      totalTTC: Math.round(item.totalTTC)
    })),
    totals: {
      montantHT: roundedHT,
      totalTVA: roundedTVA,
      montantTTC: roundedTTC,
      totalAIB,
      discount: discountAmount,
      finalTTC
    }
  };
}

/** Périodes rapides (tableau de bord / listes) — alignées sur l’API sales/stats. */
export const DUO_PERIOD_OPTIONS = [
  { label: "Aujourd'hui", value: "aujourd'hui" },
  { label: 'Hier', value: 'hier' },
  { label: '7 jours', value: 'dernier_7_jours' },
  { label: '30 jours', value: 'dernier_30_jours' },
  { label: 'Ce mois', value: 'ce_mois' },
  { label: 'Mois dernier', value: 'dernier_mois' },
];

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

/** Retourne { from, to } pour une période rapide, ou null si valeur vide. */
export const getPeriodDateRange = (period) => {
  if (!period) return null;
  const now = new Date();
  switch (period) {
    case "aujourd'hui":
      return { from: startOfDay(now), to: now };
    case 'hier': {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    }
    case 'dernier_7_jours': {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { from, to: now };
    }
    case 'dernier_30_jours': {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { from, to: now };
    }
    case 'ce_mois':
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: now };
    case 'dernier_mois': {
      const from = startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const to = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
      return { from, to };
    }
    default:
      return null;
  }
};

export const isDateInRange = (dateString, from, to) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
};